import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Hourglass } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { F, CShell, StatusPill, STATUS, STEPS, fmtDate } from "@/components/careers/ui";
import { VoiceStep } from "@/components/careers/VoiceStep";
import { InterviewStep } from "@/components/careers/InterviewStep";

const Track = ({ a }) => {
  const steps = a.status === "not_hired" ? [...STEPS.slice(0, -1), "not_hired"] : STEPS;
  const at = steps.indexOf(a.status);
  return (
    <div className="flex items-center gap-1 sm:gap-2" data-testid={`track-${a.id}`}>{steps.map((s, i) => <div key={s} className="flex-1 flex items-center gap-1 sm:gap-2">
      <div className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-xs font-bold ${i <= at ? (s === "not_hired" ? "bg-red-600 text-white" : "bg-slate-900 text-amber-400") : "bg-slate-200 text-slate-400"}`}>{i < at ? <Check className="w-4 h-4" /> : i + 1}</div>
      <span className={`hidden sm:block text-xs font-semibold ${i <= at ? "text-slate-900" : "text-slate-400"}`}>{STATUS[s][0]}</span>
      {i < steps.length - 1 && <div className={`flex-1 h-0.5 ${i < at ? "bg-slate-900" : "bg-slate-200"}`} />}</div>)}</div>
  );
};

const MSG = { submitted: "We've received your application. Our training team will review it soon.", reviewing: "Your application is being reviewed by our training team.", interview: "You've been selected for an interview! Pick a video or phone interview time below.",
  hired: "Congratulations — you're hired! Sign out and back in to open your Sales Training Academy.", not_hired: "We're not moving forward with this application. You can apply again after the 60-day waiting period." };

export default function ApplicantPortal() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const load = () => api.get("/api/careers/me").then(r => setD(r.data));
  useEffect(() => { load(); }, []);
  if (!d) return <CShell testId="applicant-portal"><p className="text-slate-400">Loading…</p></CShell>;
  const canApply = !d.reapply_at || new Date(d.reapply_at) <= new Date();
  return (
    <CShell testId="applicant-portal">
      <div><p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-600">Applicant portal</p><h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight" style={F}>Hi {d.name.split(" ")[0]}</h1></div>
      {user?.role === "sales_rep" && <Link to="/academy" className="block bg-emerald-600 text-white rounded-2xl p-5 font-bold" data-testid="portal-academy">You're hired — open the Sales Training Academy →</Link>}
      <div className={`rounded-2xl p-4 text-sm flex items-center gap-2 ${canApply ? "bg-white border border-slate-200" : "bg-slate-900 text-white"}`} data-testid="portal-reapply">
        <Hourglass className="w-4 h-4 shrink-0" />{canApply ? <>You can apply to another position. <Link to="/careers" className="font-bold underline">See open jobs</Link></> : <>You can submit a new application on <b>{fmtDate(d.reapply_at)}</b> (one application every 60 days).</>}
      </div>
      <div className="space-y-4">{d.applications.map(a => (
        <div key={a.id} className="bg-white border border-slate-200 rounded-2xl p-6 space-y-5" data-testid={`portal-app-${a.id}`}>
          <div className="flex flex-wrap items-center gap-3"><div className="flex-1"><h2 className="text-xl font-extrabold text-slate-900" style={F}>{a.job_title}</h2><p className="text-xs text-slate-500">Applied {fmtDate(a.submitted_at)}</p></div><StatusPill s={a.status} testId={`portal-status-${a.id}`} /></div>
          <Track a={a} />
          <p className="text-sm text-slate-700" data-testid={`portal-msg-${a.id}`}>{MSG[a.status]}</p>
          <InterviewStep a={a} reload={load} />
          <VoiceStep a={a} />
        </div>))}
        {!d.applications.length && <p className="text-slate-500" data-testid="portal-empty">No applications yet. <Link to="/careers" className="font-bold text-blue-700">Browse jobs</Link></p>}
      </div>
    </CShell>
  );
}
